import React, { useState } from "react";
import { useNavigate } from "react-router";
import { newAxiosClient } from "@shadowcue/ui";
import type { IUser } from "../../../types/iuser";
import {
  extractErrorMessage,
  PageContainer,
  PageTopbar,
  StickyActionBar,
  astryx,
  neutralTheme,
  useDocumentTheme,
  type BreadcrumbItem,
} from "@shadowcue/ui";
import ContactForm, { type IContactForm } from "./form";

interface NewContactProps {
  currentUser: IUser;
}

const blankContact: IContactForm = {
  firstName: "",
  lastName: "",
  email: "",
  status: "active",
};

const axios = newAxiosClient();


const NewContact = ({ currentUser }: NewContactProps) => {
  const [formValues, setFormValues] = useState<IContactForm>(blankContact);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const navigate = useNavigate();
  const mode = useDocumentTheme();

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Contacts", href: "/admin/contacts" },
    { label: "Create" },
  ];

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(
        "/api/contacts",
        { contact: formValues },
        {
          headers: { AUTHORIZATION: `Bearer ${currentUser.authToken}` },
          withCredentials: true,
        },
      );
      navigate("/admin/contacts");
    } catch (error) {
      setSubmitError(
        extractErrorMessage(error, "Failed to save the contact. Please try again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <astryx.Theme theme={neutralTheme} mode={mode}>
      <PageContainer>
        <PageTopbar breadcrumbs={breadcrumbs} />

        <ContactForm values={formValues} onChange={setFormValues} />

        <StickyActionBar error={submitError ?? undefined}>
          <astryx.Button
            label="Cancel"
            variant="ghost"
            onClick={() => navigate("/admin/contacts")}
          />
          <astryx.Button
            label="Create contact"
            variant="primary"
            isLoading={submitting}
            onClick={handleSubmit}
          />
        </StickyActionBar>
      </PageContainer>
    </astryx.Theme>
  );
};

export default NewContact;
